import type { UseQueryResult } from '@tanstack/react-query'
import {
  type InheritanceResource,
  type ReadableAccessMetadata,
  type UserLike,
} from '@opallabs/concepts'

/**
 * Takes the collaborator list of a resource and returns a tuple where the first value is
 * the individial member count across user groups and users with direct access and the
 * second value being the amount of spaces that provide membership to that resource.
 */
export function getMemberCount(
  collaborators:
    | readonly (UserLike | InheritanceResource)[]
    | Array<UserLike | InheritanceResource>
): [number, number] {
  const userIds = new Set()
  let spaceCount = 0
  collaborators.forEach((u) => {
    if (u.type === 'user_group') {
      u.members.forEach((m) => userIds.add(m.uuid))
    } else if (u.type === 'user') {
      userIds.add(u.uuid)
    } else if (u.type === 'resource' && u.inheritsMembership) {
      spaceCount++
    }
  })
  return [userIds.size, spaceCount]
}

/**
 * Storyfirst objects created before the switch from Storyfirst to access may not have a user relationship for the
 * object’s owner, so if the workspace was the only thing giving the owner access to the object, removing the
 * workspace’s access would also remove the owner’s access. This helper ensures the owner will retain access.
 *
 * When possible returns the unmodified `access` object to preserve referential integrity.
 */
export function ensureOwnerIsInAccess(
  access: UseQueryResult<ReadableAccessMetadata | undefined>,
  ownerUuid: string | undefined
): UseQueryResult<ReadableAccessMetadata | undefined> {
  if (access.data === undefined || !ownerUuid) return access

  const ownerExists = access.data.users.some(({ id }) => id === ownerUuid)
  if (ownerExists) return access

  return {
    ...access,
    data: {
      ...access.data,
      users: [...access.data.users, { id: ownerUuid, type: 'user' as const }],
    },
  } satisfies UseQueryResult<ReadableAccessMetadata | undefined>
}
